import {Component, computed, inject, input, signal, ChangeDetectionStrategy} from "@angular/core"
import {ActionPanelComponent} from "@app/common/components/menu/actions/action-panel/action-panel.component"
import {ActionPanelRowComponent} from "@app/common/components/menu/actions/action-panel-row/action-panel-row.component"
import {FilesizePipe} from "@app/common/pipes/filesize/filesize.pipe"
import {ActionBarComponent} from "@app/common/components/menu/actions/action-bar/action-bar.component"
import {ActionItemComponent} from "@app/common/components/menu/actions/action-item/action-item.component"
import {MatTooltipModule} from "@angular/material/tooltip"
import {MatMenuModule} from "@angular/material/menu"
import {TemplateImageViewerCanvasComponent} from "@app/template-editor/components/template-image-viewer-canvas/template-image-viewer-canvas.component"
import {isMobileDevice} from "@cm/browser-utils"
import {FilesService} from "@app/common/services/files/files.service"
import {CanvasNavigationLegacy} from "@common/helpers/canvas/canvas-navigation-legacy"
import {ImageProcessingNodes} from "@cm/image-processing-nodes"
import UTIF from "utif2"
import {PermissionsService} from "@app/common/services/permissions/permissions.service"
import {ImageProcessingService} from "@app/common/services/rendering/image-processing.service"
import {firstValueFrom} from "rxjs"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-image-viewer-controls",
    templateUrl: "./template-image-viewer-controls.component.html",
    styleUrl: "./template-image-viewer-controls.component.scss",
    imports: [ActionPanelComponent, ActionPanelRowComponent, FilesizePipe, ActionBarComponent, ActionItemComponent, MatTooltipModule, MatMenuModule],
})
export class TemplateImageViewerControlsComponent {
    readonly $parent = input.required<TemplateImageViewerCanvasComponent>({alias: "parent"})
    readonly $fileTitle = input<string>(undefined, {alias: "fileTitle"})

    private readonly imageProcessingService = inject(ImageProcessingService)
    private readonly permission = inject(PermissionsService)
    readonly $can = this.permission.$to

    readonly $isMobileDevice = signal(isMobileDevice)

    readonly $postProcessedImageData = computed(() => {
        return this.$parent().$postProcessedImageData()
    })
    readonly $canvasBase = computed(() => {
        return this.$parent().$canvasBase()
    })
    navigation = (): CanvasNavigationLegacy | undefined => {
        return this.$canvasBase().navigation
    }

    readonly $imageData = computed(() => {
        const postProcessedImageData = this.$postProcessedImageData()
        return postProcessedImageData.imageData
    })
    readonly $rawImageData = computed(() => {
        const postProcessedImageData = this.$postProcessedImageData()
        return postProcessedImageData.rawImageData
    })

    readonly $size = computed(() => {
        return this.$imageData().data.length
    })
    readonly $width = computed(() => {
        return this.$imageData().width
    })
    readonly $height = computed(() => {
        return this.$imageData().height
    })

    readonly zoomLevel = computed(() => Math.round((this.$canvasBase()?.$zoomLevel() ?? 1) * devicePixelRatio * 100))

    async downloadFromImageData(format: "png" | "jpeg" | "tiff" | "exr") {
        const imageData = this.$imageData()

        if (format === "tiff") {
            const tiffData = UTIF.encodeImage(new Uint8Array(imageData.data.buffer), imageData.width, imageData.height)
            const blob = new Blob([tiffData], {type: "image/tiff"})
            FilesService.downloadFile(`${this.$fileTitle() || "snapshot"}.tiff`, URL.createObjectURL(blob))

            return
        } else if (format === "exr") {
            const rawImageData = this.$rawImageData()

            const graph: ImageProcessingNodes.Encode = {
                type: "encode",
                input: rawImageData.combinedPass,
                mediaType: "image/x-exr",
            }

            const result = await firstValueFrom(this.imageProcessingService.evalGraph(graph))
            const blob = new Blob([new Uint8Array(result.data)], {type: "image/x-exr"})
            FilesService.downloadFile(`${this.$fileTitle() || "snapshot"}.exr`, URL.createObjectURL(blob))

            return
        }

        const canvas = document.createElement("canvas")
        canvas.width = imageData.width
        canvas.height = imageData.height

        const context = canvas.getContext("2d")
        if (!context) return

        const type = `image/${format}`
        const ext = format === "jpeg" ? "jpg" : "png"

        context.putImageData(imageData, 0, 0)
        const dataUrl = canvas.toDataURL(type)
        FilesService.downloadFile(`${this.$fileTitle() || "snapshot"}.${ext}`, dataUrl.replace(type, "image/octet-stream"))
    }
}
